import React, { createContext, useContext, useEffect, useState } from "react";
import {
  HttpTransportType,
  HubConnectionBuilder,
  LogLevel,
} from "@microsoft/signalr";
import { notification } from "antd";

import {
  getAccessToken,
  getSessionData,
} from "../../utils/interceptors/localStorageService";
import { getWebSocketUrl } from "../config/tenantConfiguration";
import { saveNotificationsToSession } from "../../utils/helper/util";
import { useNotification } from "../../store/context/notification-context";

const WebSocketContext = createContext(null);

const URL = getWebSocketUrl(); 

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { setSignalRNotification } = useNotification();
  // Initialize with null and then update after checking session
  const [connection, setConnection] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [userId, setUserId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize access token
  useEffect(() => {
    const initializeToken = () => {
      const sessionData = getSessionData();
      if (sessionData?.accessToken) {
        setAccessToken(sessionData.accessToken);
      }
      if (sessionData?.accessToken) {
        setUserId(sessionData.userId);
      }
    };

    initializeToken();
    setIsInitialized(true);
  }, []);

  const handleMessage = (messageRes) => {
    const {
      messageType,
      status,
      action,
      loanRequestId,
      transactionId,
      message,
    } = messageRes;
    // console.log("message ---- ", message);

    setSignalRNotification(messageRes);
    saveNotificationsToSession(userId, {
      ...messageRes,
      id: Date.now(),
      timestamp: new Date().toISOString(),
    });

    if (action?.toLowerCase() === "initiated") {
      notification.info({
        message: `Transaction ${action}`,
        description: message,
        placement: "topRight",
      });
    } else if (action?.toLowerCase() === "transactionupdate") {
      if (status === "success") {
        notification.success({
          message: `Transaction ${status}`,
          description: message,
          placement: "topRight",
        });
      } else if (status === "failed") {
        notification.error({
          message: `Transaction ${status}`,
          description: message,
          placement: "topRight",
        });
      } else {
        notification.info({
          message: `Transaction ${status}`,
          description: message,
          placement: "topRight",
        });
      }
    } else if (action?.toLowerCase() === "approvalrequired") {
      notification.info({
        message: "Approval Required",
        description: `A loan request requires your approval. Loan Request ID: ${loanRequestId}`,
        placement: "topRight",
        btn: (
          <button
            onClick={() =>
              console.log(`Viewing Loan Request ID: ${loanRequestId}`)
            }
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800"
          >
            View Loan Request
          </button>
        ),
      });
    }
  };

  const startConnection = async () => {
    // Don't attempt connection if there's no access token
    if (!accessToken) {
      // console.log('No access token available, skipping connection');
      return;
    }

    try {
      if (connection) {
        await connection.stop();
      }

      const newConnection = new HubConnectionBuilder()
        .withUrl(URL, {
          accessTokenFactory: () => accessToken,
          transport:
            HttpTransportType.WebSockets | HttpTransportType.LongPolling,
        })
        .configureLogging(LogLevel.Information)
        .build();

      newConnection.on("ReceiveNotification", handleMessage);
      newConnection.on("ReceiveTransactionStatus", handleMessage);

      await newConnection.start();
      // console.log('SignalR connection established');
      setConnection(newConnection);
    } catch (error) {
      console.error("SignalR Connection Error:", error);
      // Only retry if we still have an access token
      if (accessToken) {
        setTimeout(startConnection, 5000);
      }
    }
  };

  // Effect to monitor access token changes
  useEffect(() => {
    const checkAccessToken = () => {
      const sessionData = getSessionData();
      const currentToken = sessionData?.accessToken || null;
      const currentUserId = sessionData?.userId || null;

      if (currentToken !== accessToken) {
        setAccessToken(currentToken);
      }
      if (currentUserId !== userId) {
        setUserId(currentUserId);
      }
    };

    // Check for token changes every minute
    const tokenCheckInterval = setInterval(checkAccessToken, 60000);

    return () => {
      clearInterval(tokenCheckInterval);
    };
  }, [accessToken, userId]);

  // Effect to manage connection based on access token
  useEffect(() => {
    if (isInitialized && accessToken && userId) {
      startConnection();
    } else if (!accessToken && connection) {
      // Disconnect if token is removed
      connection.stop();
      setConnection(null);
    }

    return () => {
      if (connection) {
        connection.stop();
      }
    };
  }, [accessToken, userId, isInitialized]);

  const value = {
    isConnected: connection?.state === "Connected",
    connection,
    isInitialized,
    hasToken: !!accessToken,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
